import { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase/config';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc, query, getDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import Logo from '../components/Logo';
import { 
  PencilIcon, 
  TrashIcon, 
  PlusIcon,
  XMarkIcon,
  CircleStackIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { updatePaymentSettings, usePaymentSettings } from '../firebase/paymentSettings';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AdminWithdrawalNotifications from '../components/AdminWithdrawalNotifications';

function AdminDashboard() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [allIdCards, setAllIdCards] = useState([]);
  const [unassignedIdCards, setUnassignedIdCards] = useState([]);
  const [loadingIdCards, setLoadingIdCards] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const paymentSettings = usePaymentSettings();
  const [settings, setSettings] = useState({
    crypto: {
      bitcoin: {
        address: '',
        name: 'Bitcoin (BTC)'
      },
      ethereum: {
        address: '',
        name: 'Ethereum (ETH)'
      },
      usdt: {
        address: '',
        name: 'USDT (TRC20)'
      }
    },
    cashapp: {
      cashtag: '',
      instructions: [
        'Open your CashApp',
        'Click the Pay button',
        'Enter our cashtag',
        'Enter the amount you wish to deposit',
        'Add your account ID in the notes',
        'Confirm the payment'
      ]
    },
    paypal: {
      email: '',
      instructions: [
        'Log in to your PayPal account',
        'Click Send & Request',
        'Enter our PayPal email',
        'Enter the amount you wish to deposit',
        'Add your account ID in the notes',
        'Review and Send payment'
      ]
    },
    bank: {
      details: {
        bankName: '',
        accountName: '',
        accountNumber: '',
        routingNumber: '',
        swift: ''
      }
    }
  });
  const inputRefs = useRef({});
  const [migrationStatus, setMigrationStatus] = useState('');
  const [existingIdCards, setExistingIdCards] = useState([]);
  const [manualIdCards, setManualIdCards] = useState([
    {
      url: 'https://ibb.co/RkSV8nfD',
      email: 'albright.financing@gmail.com', // Replace with actual email
      name: 'David Albright'
    },
    {
      url: 'https://ibb.co/1tCNmpJG',
      email: 'pappymckinney@gmail.com', // Replace with actual email
      name: 'Sterling MCKINNEY'
    },
    {
      url: 'https://ibb.co/fVg35r5j',
      email: 'nthorpe96@gmail.com', // Replace with actual email
      name: 'Nicole Thorpe'
    }
  ]);

  const fetchAllIdCards = async () => {
    try {
      setLoadingIdCards(true);
      const idCards = [];
      
      // Get all users from Firestore
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Starting ID card fetch...');
      console.log('Total users found:', users.length);
      
      // Check for ID cards in user documents
      users.forEach(user => {
        // Check various possible locations for ID card URL
        const idCardUrl = user.idCardUrl || 
                         user.idCard || 
                         user.accountDetails?.idCard || 
                         user.accountDetails?.idCardUrl ||
                         user.verification?.idCard ||
                         user.verification?.idCardUrl ||
                         user.documents?.idCard ||
                         user.documents?.idCardUrl ||
                         user.kyc?.idCard ||
                         user.kyc?.idCardUrl ||
                         user.profile?.idCard ||
                         user.profile?.idCardUrl;

        if (idCardUrl) {
          console.log(`Found ID card URL for user ${user.email}:`, idCardUrl);
          idCards.push({
            id: user.id,
            email: user.email,
            fullName: user.fullName || user.name,
            idCardUrl: idCardUrl,
            idCardDeleteUrl: user.idCardDeleteUrl || null,
            idCardId: user.idCardId || null,
            createdAt: user.createdAt || new Date().toISOString()
          });
        } else {
          console.log(`No ID card found for user ${user.email}`);
        }
      });

      // Add manual ID cards
      manualIdCards.forEach(manualCard => {
        const user = users.find(u => u.email === manualCard.email);
        if (user) {
          idCards.push({
            id: user.id,
            email: user.email,
            fullName: user.fullName || user.name,
            idCardUrl: manualCard.url,
            idCardDeleteUrl: null,
            idCardId: null,
            createdAt: user.createdAt || new Date().toISOString()
          });
        }
      });
      
      console.log(`\nTotal valid ID cards found: ${idCards.length}`);
      console.log('ID cards array:', idCards);
      setAllIdCards(idCards);
      setUnassignedIdCards([]);
    } catch (error) {
      console.error('Error fetching ID cards:', error);
      // Show error to user
      alert('Error fetching ID cards. Please check console for details.');
    } finally {
      setLoadingIdCards(false);
    }
  };

  const getHighQualityImageUrl = (url) => {
    if (!url) return url;
    
    // If it's an ImgBB URL, modify it to get the highest quality version
    if (url.includes('ibb.co')) {
      // Remove any existing size parameters
      url = url.split('?')[0];
      // Add parameters for maximum quality
      return `${url}?maxwidth=2000&maxheight=2000&quality=100`;
    }
    
    return url;
  };

  const getImgBBPageUrl = (url) => {
    if (!url) return url;
    
    // Convert direct image URL to ImgBB page URL
    if (url.includes('i.ibb.co')) {
      // Extract the image ID from the URL
      const parts = url.split('/');
      const imageId = parts[parts.length - 2]; // Get the second to last part
      return `https://ibb.co/${imageId}`;
    }
    
    return url;
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    fetchUsers();
    fetchAllIdCards();
    if (paymentSettings) {
      setSettings(paymentSettings);
    }
  }, [isAdmin, navigate, paymentSettings]);

  const fetchUsers = async () => {
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        idCardUrl: doc.data().idCardUrl || null,
        idCardDeleteUrl: doc.data().idCardDeleteUrl || null,
        idCardId: doc.data().idCardId || null,
        registrationDate: doc.data().registrationDate || doc.data().createdAt || null
      }));
      
      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        // Get user data before deletion
        const userDoc = await getDoc(doc(db, 'users', userId));
        const userData = userDoc.data();

        // Delete user's ID card from ImgBB if exists
        if (userData.idCardDeleteUrl) {
          try {
            await fetch(userData.idCardDeleteUrl, { method: 'DELETE' });
            console.log('ID card image deleted from ImgBB');
          } catch (error) {
            console.error('Error deleting ID card image:', error);
          }
        }

        // Delete user document
        await deleteDoc(doc(db, 'users', userId));
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(error.message);
      }
    }
  };

  const cleanupOldImages = async () => {
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get all image IDs from ImgBB
      const response = await fetch('https://api.imgbb.com/1/images?key=5b3e260b02831873f7aeb434d1d64df3');
      const data = await response.json();
      
      if (data.success) {
        const activeImageIds = users.map(user => user.idCardId).filter(Boolean);
        const imagesToDelete = data.data.images.filter(img => !activeImageIds.includes(img.id));

        // Delete unused images
        for (const image of imagesToDelete) {
          try {
            await fetch(image.delete_url, { method: 'DELETE' });
            console.log(`Deleted unused image: ${image.id}`);
          } catch (error) {
            console.error(`Error deleting image ${image.id}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Error cleaning up images:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updatePaymentSettings(settings);
    setSaving(false);
    setSaveStatus(result.success ? 'success' : 'error');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const CryptoAddressInput = ({ coin, coinData, isLast }) => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const nextCoin = Object.keys(settings.crypto).find((key, index, arr) => {
          return arr[index - 1] === coin;
        });
        if (nextCoin && inputRefs.current[nextCoin]) {
          inputRefs.current[nextCoin].focus();
        } else if (isLast) {
          const cashAppInput = document.querySelector('input[name="cashtag"]');
          if (cashAppInput) cashAppInput.focus();
        }
      }
    };

    const handleChange = (e) => {
      const value = e.target.value;
      setSettings(prev => ({
        ...prev,
        crypto: {
          ...prev.crypto,
          [coin]: {
            ...prev.crypto[coin],
            address: value
          }
        }
      }));
    };

    return (
      <div className="space-y-2 p-4 border rounded-lg bg-white hover:shadow-md transition-shadow">
        <label className="block text-sm font-medium text-gray-700">
          {coinData.name} Address
        </label>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                ref={el => inputRefs.current[coin] = el}
                type="text"
                value={coinData.address}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                placeholder={`Enter ${coinData.name} address`}
                autoComplete="off"
              />
              {coinData.address && (
                <button
                  onClick={() => {
                    const input = inputRefs.current[coin];
                    input.select();
                    input.setSelectionRange(0, 99999);
                    navigator.clipboard.writeText(coinData.address);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-primary-600 hover:text-primary-700 p-1"
                  title="Copy address"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                </button>
              )}
            </div>
            {coinData.address && (
              <p className="mt-1 text-xs text-gray-500">
                Press Enter to move to next field
              </p>
            )}
          </div>
          {coinData.address && (
            <div className="w-32 h-32 bg-white rounded-lg p-2 border">
              <QRCodeSVG
                value={coinData.address}
                size={112}
                level="H"
                includeMargin={true}
                className="w-full h-full"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const UserDetailsModal = ({ user, onClose, onDelete }) => {
    const [imageError, setImageError] = useState(false);

    const handleImageError = () => {
      setImageError(true);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">User Details</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
              <div>
              <p className="text-sm text-gray-500">Full Name</p>
              <p className="font-medium">{user.fullName}</p>
                </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{user.email}</p>
              </div>
              <div>
              <p className="text-sm text-gray-500">Mobile Number</p>
              <p className="font-medium">{user.mobileNumber}</p>
              </div>
              <div>
              <p className="text-sm text-gray-500">Country</p>
              <p className="font-medium">{user.country}</p>
              </div>
              <div>
              <p className="text-sm text-gray-500">Date of Birth</p>
              <p className="font-medium">{user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : 'Not provided'}</p>
              </div>
              <div>
              <p className="text-sm text-gray-500">Employment Status</p>
              <p className="font-medium">{user.employmentStatus || 'Not provided'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-500">Home Address</p>
              <p className="font-medium">{user.homeAddress || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Registration Date</p>
              <p className="font-medium">{new Date(user.registrationDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Account Status</p>
              <p className="font-medium">{user.accountDetails?.status || 'pending'}</p>
              </div>
            </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Investment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                <p className="text-sm text-gray-500">Investment Plan</p>
                <p className="font-medium">{user.accountDetails?.investmentPlan || 'Not selected'}</p>
                </div>
                <div>
                <p className="text-sm text-gray-500">Total Investment</p>
                <p className="font-medium">${user.accountDetails?.totalInvestment?.toLocaleString() || '0'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Profit</p>
                <p className="font-medium">${user.accountDetails?.totalProfit?.toLocaleString() || '0'}</p>
                </div>
                <div>
                <p className="text-sm text-gray-500">Available Balance</p>
                <p className="font-medium">${user.accountDetails?.availableBalance?.toLocaleString() || '0'}</p>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-medium mb-2">Active Investments</h4>
              {user.investments?.length > 0 ? (
                user.investments.map((investment, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg mb-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                        <p className="text-sm text-gray-500">Plan</p>
                        <p className="font-medium">{investment.planName}</p>
                </div>
                <div>
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="font-medium">${investment.amount?.toLocaleString() || '0'}</p>
                </div>
                      <div>
                        <p className="text-sm text-gray-500">Expected Return</p>
                        <p className="font-medium">${investment.expectedReturn?.toLocaleString() || '0'}</p>
              </div>
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="font-medium">{investment.status}</p>
            </div>
                      <div>
                        <p className="text-sm text-gray-500">Start Date</p>
                        <p className="font-medium">{new Date(investment.startDate).toLocaleDateString()}</p>
          </div>
                      <div>
                        <p className="text-sm text-gray-500">End Date</p>
                        <p className="font-medium">{new Date(investment.endDate).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No active investments</p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">ID Card</h3>
            {user.idCardUrl ? (
              <div className="mt-2">
                {!imageError ? (
                  <img
                    src={user.idCardUrl}
                    alt="ID Card"
                    className="max-w-full h-auto rounded-lg shadow-md"
                    onError={handleImageError}
                  />
                ) : (
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <p className="text-gray-500">Failed to load image</p>
                    <p className="text-sm text-gray-400">Image URL: {user.idCardUrl}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No ID card uploaded</p>
            )}
          </div>

          <div className="mt-4 flex justify-end space-x-4">
            <button
              onClick={() => {
                onDelete(user.id);
                onClose();
              }}
              className="text-red-600 hover:text-red-800"
            >
              Delete User
            </button>
        </div>
      </div>
    </div>
  );
  };

  const ImageModal = ({ image, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">ID Card Details</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-4 overflow-auto">
          <img
            src={getHighQualityImageUrl(image.idCardUrl)}
            alt="ID Card"
            className="w-full h-auto max-h-[70vh] object-contain"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x500?text=ID+Card+Not+Available';
            }}
          />
        </div>
      </div>
    </div>
  );

  const IdCardGrid = ({ cards, title }) => (
    <div className="mb-8">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card) => (
          <div key={card.id} className="border rounded-lg overflow-hidden bg-white">
            <div className="p-4 bg-gray-50">
              <h3 className="font-medium text-gray-900">{card.fullName || 'Unassigned ID Card'}</h3>
              {card.email && <p className="text-sm text-gray-500">{card.email}</p>}
              <p className="text-xs text-gray-400">
                {card.createdAt ? new Date(card.createdAt).toLocaleDateString() : 'Date unknown'}
              </p>
            </div>
            <div className="relative aspect-[4/3] bg-gray-100">
              <img
                src={getHighQualityImageUrl(card.idCardUrl)}
                alt={`ID Card for ${card.fullName || 'Unassigned'}`}
                className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => {
                  setSelectedImage(card);
                  setShowImageModal(true);
                }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x500?text=ID+Card+Not+Available';
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const handleExistingIdCards = async (links) => {
    try {
      setMigrationStatus('Processing existing ID card links...');
      const idCards = links.split('\n').map(link => link.trim()).filter(link => link);
      setExistingIdCards(idCards);
      setMigrationStatus(`Found ${idCards.length} ID card links. Click "Migrate ID Card Data" to process them.`);
    } catch (error) {
      console.error('Error processing ID card links:', error);
      setMigrationStatus('Error processing ID card links. Check console for details.');
    }
  };

  const migrateIdCardData = async () => {
    try {
      setMigrationStatus('Starting migration...');
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      let migratedCount = 0;
      let skippedCount = 0;
      
      for (const doc of querySnapshot.docs) {
        const userData = doc.data();
        
        // Check if user already has the new ID card fields
        if (userData.idCardUrl && userData.idCardDeleteUrl && userData.idCardId) {
          skippedCount++;
          continue;
        }
        
        // First check if we have a pre-existing link for this user
        const existingLink = existingIdCards.find(link => 
          link.includes(userData.email) || 
          link.includes(userData.fullName?.toLowerCase())
        );

        if (existingLink) {
          // Extract image ID from URL
          const parts = existingLink.split('/');
          const imageId = parts[parts.length - 2];
          
          // Update user document with new ID card fields
          await updateDoc(doc.ref, {
            idCardUrl: existingLink,
            idCardDeleteUrl: `https://api.imgbb.com/1/images/${imageId}/delete?key=5b3e260b02831873f7aeb434d1d64df3`,
            idCardId: imageId,
            idCardUploadDate: userData.createdAt || new Date().toISOString()
          });
          
          migratedCount++;
          console.log(`Migrated ID card data for user ${userData.email} using pre-existing link`);
          continue;
        }
        
        // If no pre-existing link, try to find ID card URL in various possible locations
        const idCardUrl = userData.idCardUrl || 
                         userData.idCard || 
                         userData.accountDetails?.idCard || 
                         userData.accountDetails?.idCardUrl ||
                         userData.verification?.idCard ||
                         userData.verification?.idCardUrl ||
                         userData.documents?.idCard ||
                         userData.documents?.idCardUrl ||
                         userData.kyc?.idCard ||
                         userData.kyc?.idCardUrl ||
                         userData.profile?.idCard ||
                         userData.profile?.idCardUrl;

        if (idCardUrl && idCardUrl.includes('ibb.co')) {
          // Extract image ID from URL
          const parts = idCardUrl.split('/');
          const imageId = parts[parts.length - 2];
          
          // Update user document with new ID card fields
          await updateDoc(doc.ref, {
            idCardUrl: idCardUrl,
            idCardDeleteUrl: `https://api.imgbb.com/1/images/${imageId}/delete?key=5b3e260b02831873f7aeb434d1d64df3`,
            idCardId: imageId,
            idCardUploadDate: userData.createdAt || new Date().toISOString()
          });
          
          migratedCount++;
          console.log(`Migrated ID card data for user ${userData.email}`);
        } else {
          skippedCount++;
        }
      }
      
      setMigrationStatus(`Migration complete. ${migratedCount} users updated, ${skippedCount} users skipped.`);
      alert(`Migration complete. ${migratedCount} users updated, ${skippedCount} users skipped.`);
      fetchUsers();
      fetchAllIdCards();
    } catch (error) {
      console.error('Error migrating ID card data:', error);
      setMigrationStatus('Error during migration. Check console for details.');
      alert('Error migrating ID card data. Check console for details.');
    }
  };

  const fixIdCardUrls = async () => {
    try {
      setMigrationStatus('Starting ID card URL fix...');
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      let fixedCount = 0;
      let skippedCount = 0;
      
      for (const doc of querySnapshot.docs) {
        const userData = doc.data();
        
        // Skip if user doesn't have an ID card URL
        if (!userData.idCardUrl) {
          skippedCount++;
          continue;
        }

        // Fix the URL format
        let fixedUrl = userData.idCardUrl;
        if (fixedUrl.includes('ibb.co')) {
          // Convert to direct image URL if it's a page URL
          if (!fixedUrl.includes('i.ibb.co')) {
            const parts = fixedUrl.split('/');
            const imageId = parts[parts.length - 2];
            fixedUrl = `https://i.ibb.co/${imageId}/${parts[parts.length - 1]}`;
          }
        }

        // Extract image ID from URL
        const urlParts = fixedUrl.split('/');
        const imageId = urlParts[urlParts.length - 2];

        // Create delete URL
        const deleteUrl = `https://api.imgbb.com/1/images/${imageId}/delete?key=5b3e260b02831873f7aeb434d1d64df3`;

        // Update user document with fixed fields
        await updateDoc(doc.ref, {
          idCardUrl: fixedUrl,
          idCardDeleteUrl: deleteUrl,
          idCardId: imageId,
          idCardUploadDate: userData.idCardUploadDate || userData.createdAt || new Date().toISOString()
        });
        
        fixedCount++;
        console.log(`Fixed ID card data for user ${userData.email}`);
      }
      
      setMigrationStatus(`Fix complete. ${fixedCount} users updated, ${skippedCount} users skipped.`);
      alert(`Fix complete. ${fixedCount} users updated, ${skippedCount} users skipped.`);
      fetchUsers();
      fetchAllIdCards();
    } catch (error) {
      console.error('Error fixing ID card URLs:', error);
      setMigrationStatus('Error during fix. Check console for details.');
      alert('Error fixing ID card URLs. Check console for details.');
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <button
                onClick={() => auth.signOut()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Sign Out
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('users')}
                  className={`${
                    activeTab === 'users'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  User Management
                </button>
                <button
                  onClick={() => setActiveTab('idCards')}
                  className={`${
                    activeTab === 'idCards'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  ID Card Management
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`${
                    activeTab === 'payments'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Payment Settings
                </button>
              </nav>
            </div>

            {/* Content */}
            {activeTab === 'users' ? (
              <>
          {/* Withdrawal Notifications Section */}
          <div className="mb-8">
            <AdminWithdrawalNotifications />
          </div>

          {/* User Management Section */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create User</span>
          </button>
        </div>
        
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Card</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Invested</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Profit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OTP Codes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Investments</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => {
                          console.log('Rendering user:', user);
                          return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{user.fullName || user.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.idCardUrl ? (
                        <div className="flex items-center">
                          <img
                            src={user.idCardUrl}
                            alt="ID Card"
                            className="h-10 w-16 object-cover rounded cursor-pointer"
                            onClick={() => {
                                      console.log('Opening modal for user:', user);
                              setSelectedUser(user);
                              setUserToEdit(user);
                            }}
                                    onError={(e) => {
                                      console.error('Image load error:', e);
                                      e.target.parentElement.innerHTML = 'Image load failed';
                            }}
                          />
                        </div>
                      ) : (
                                <span className="text-gray-400">
                                  No ID {user.idCardUrl ? '(URL exists but failed to load)' : '(No URL)'}
                                </span>
                      )}
                    </td>
                            <td className="px-6 py-4 whitespace-nowrap">${user.balance?.toLocaleString() || '0'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">${user.totalInvested?.toLocaleString() || '0'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">${user.totalProfit?.toLocaleString() || '0'}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="font-mono text-xs mb-1">
                          <span className="font-medium text-gray-600">Login:</span>{' '}
                          <span className="bg-gray-100 px-1 py-0.5 rounded">
                            {user.securityDetails?.loginOTP || 'Not set'}
                          </span>
                        </p>
                        <p className="font-mono text-xs">
                          <span className="font-medium text-gray-600">WDL:</span>{' '}
                          <span className="bg-gray-100 px-1 py-0.5 rounded">
                            {user.securityDetails?.withdrawalOTP || 'Not set'}
                          </span>
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {user.investments?.filter(inv => inv.status === 'active').length || 0} Active
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          setUserToEdit(user);
                          setShowEditModal(true);
                        }}
                          className="text-primary-600 hover:text-primary-900"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      </div>
                    </td>
                  </tr>
                        );
                        })}
              </tbody>
            </table>
            </div>
          </div>
              </>
            ) : activeTab === 'idCards' ? (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">ID Card Management</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Manage and view all ID cards uploaded by users
                  </p>
                </div>

                {loadingIdCards ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    <IdCardGrid
                      cards={allIdCards}
                      title="All ID Cards"
                    />
                  </div>
                )}

                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Manual ID Card Assignments</h3>
                  <div className="space-y-4">
                    {manualIdCards.map((card, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{card.name}</p>
                          <p className="text-sm text-gray-500">{card.email}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={card.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            View ID Card
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Payment Settings</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Manage payment methods and settings
                  </p>
                </div>

                <div className="space-y-8">
                  {/* Crypto Settings */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Cryptocurrency Addresses</h3>
                    <div className="space-y-4">
                      {Object.entries(settings.crypto).map(([coin, coinData], index, arr) => (
                        <CryptoAddressInput
                          key={coin}
                          coin={coin}
                          coinData={coinData}
                          isLast={index === arr.length - 1}
                        />
                      ))}
                    </div>
                  </div>

                  {/* CashApp Settings */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">CashApp Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">CashApp Cashtag</label>
                        <input
                          type="text"
                          name="cashtag"
                          value={settings.cashapp.cashtag}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            cashapp: { ...prev.cashapp, cashtag: e.target.value }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Instructions</label>
                        <div className="mt-1 space-y-2">
                          {settings.cashapp.instructions.map((instruction, index) => (
                            <div key={index} className="flex items-start">
                              <span className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                                {index + 1}
                              </span>
                              <p className="ml-3 text-sm text-gray-500">{instruction}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* PayPal Settings */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">PayPal Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">PayPal Email</label>
                        <input
                          type="email"
                          value={settings.paypal.email}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            paypal: { ...prev.paypal, email: e.target.value }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Instructions</label>
                        <div className="mt-1 space-y-2">
                          {settings.paypal.instructions.map((instruction, index) => (
                            <div key={index} className="flex items-start">
                              <span className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                                {index + 1}
                              </span>
                              <p className="ml-3 text-sm text-gray-500">{instruction}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                        <input
                          type="text"
                          value={settings.bank.details.bankName}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, bankName: e.target.value }
                            }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Account Name</label>
                        <input
                          type="text"
                          value={settings.bank.details.accountName}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, accountName: e.target.value }
                            }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Account Number</label>
                        <input
                          type="text"
                          value={settings.bank.details.accountNumber}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, accountNumber: e.target.value }
                            }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Routing Number</label>
                        <input
                          type="text"
                          value={settings.bank.details.routingNumber}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, routingNumber: e.target.value }
                            }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">SWIFT Code</label>
                        <input
                          type="text"
                          value={settings.bank.details.swift}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, swift: e.target.value }
                            }
                          }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                        saving ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>

                  {/* Save Status */}
                  {saveStatus && (
                    <div className={`mt-4 p-4 rounded-md ${
                      saveStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      {saveStatus === 'success' ? 'Settings saved successfully!' : 'Error saving settings. Please try again.'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {showEditModal && (
        <EditUserModal 
          user={userToEdit}
          onClose={() => {
            setShowEditModal(false);
            setUserToEdit(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setUserToEdit(null);
            fetchUsers();
          }}
        />
      )}

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDelete={handleDeleteUser}
        />
      )}

      {showImageModal && selectedImage && (
        <ImageModal
          image={selectedImage}
          onClose={() => {
            setShowImageModal(false);
            setSelectedImage(null);
          }}
        />
      )}
    </div>
  );
}

// Separate component definitions outside the main component
const CreateUserModal = ({ onClose, onSuccess }) => {
  const [localFormData, setLocalFormData] = useState({
    email: '',
    password: '',
    name: '',
    balance: 0,
    totalInvested: 0,
    totalProfit: 0,
    portfolio: {
      stocks: 0,
      crypto: 0,
      realEstate: 0,
      cash: 0
    },
    investments: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        localFormData.email,
        localFormData.password
      );

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: localFormData.email,
        name: localFormData.name,
        createdAt: new Date().toISOString(),
        balance: parseFloat(localFormData.balance) || 0,
        totalInvested: parseFloat(localFormData.totalInvested) || 0,
        totalProfit: parseFloat(localFormData.totalProfit) || 0,
        portfolio: {
          stocks: parseFloat(localFormData.portfolio.stocks) || 0,
          crypto: parseFloat(localFormData.portfolio.crypto) || 0,
          realEstate: parseFloat(localFormData.portfolio.realEstate) || 0,
          cash: parseFloat(localFormData.portfolio.cash) || 0
        },
        investments: [],
        status: 'active',
        accountDetails: {
          lastLogin: new Date().toISOString(),
          accountType: 'standard',
          verificationStatus: 'verified'
        }
      });

      onSuccess();
    } catch (error) {
      console.error("Error creating user:", error);
      alert(error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Create New User</h2>
          <button onClick={onClose}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={localFormData.email}
              onChange={(e) => setLocalFormData(prev => ({...prev, email: e.target.value}))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input
              type="password"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={localFormData.password}
              onChange={(e) => setLocalFormData(prev => ({...prev, password: e.target.value}))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={localFormData.name}
              onChange={(e) => setLocalFormData(prev => ({...prev, name: e.target.value}))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Balance</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={localFormData.balance}
                onChange={(e) => setLocalFormData(prev => ({...prev, balance: e.target.value}))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Invested</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={localFormData.totalInvested}
                onChange={(e) => setLocalFormData(prev => ({...prev, totalInvested: e.target.value}))}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio Distribution</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700">Stocks (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.stocks}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, stocks: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Crypto (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.crypto}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, crypto: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Real Estate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.realEstate}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, realEstate: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Cash (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.cash}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, cash: e.target.value}
                  }))}
                />
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700"
          >
            Create User
          </button>
        </form>
      </div>
    </div>
  );
};

const EditUserModal = ({ user, onClose, onSuccess }) => {
  const [localFormData, setLocalFormData] = useState({
    balance: user?.balance || 0,
    totalInvested: user?.totalInvested || 0,
    totalProfit: user?.totalProfit || 0,
    portfolio: {
      stocks: user?.portfolio?.stocks || 0,
      crypto: user?.portfolio?.crypto || 0,
      realEstate: user?.portfolio?.realEstate || 0,
      cash: user?.portfolio?.cash || 0
    },
    investments: user?.investments || []
  });

  const [newInvestment, setNewInvestment] = useState({
    planName: '',
    amount: '',
    startDate: '',
    endDate: '',
    status: 'active'
  });

  if (!user || !user.id) {
    console.error('Invalid user data provided to EditUserModal');
    onClose();
    return null;
  }

  const handleAddInvestment = () => {
    if (!newInvestment.planName || !newInvestment.amount || !newInvestment.startDate) {
      alert('Please fill in all investment fields');
      return;
    }

    // Calculate end date based on 21-day period
    const startDate = new Date(newInvestment.startDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 21);

    // Calculate expected return based on plan
    let expectedReturn = 0;
    const amount = parseFloat(newInvestment.amount);
    switch(newInvestment.planName) {
      case 'Standard Plan':
        if (amount >= 1000 && amount <= 4000) {
          expectedReturn = amount * 10;
        }
        break;
      case 'Premium Plan':
        if (amount >= 5000 && amount <= 9000) {
          expectedReturn = amount * 15;
        }
        break;
      case 'Advanced Plan':
        if (amount >= 10000) {
          expectedReturn = amount * 20;
        }
        break;
      default:
        expectedReturn = 0;
    }

    setLocalFormData(prev => ({
      ...prev,
      investments: [...prev.investments, { 
        ...newInvestment, 
        id: Date.now().toString(),
        endDate: endDate.toISOString().split('T')[0],
        expectedReturn: expectedReturn
      }]
    }));

    setNewInvestment({
      planName: '',
      amount: '',
      startDate: '',
      status: 'active'
    });
  };

  const handleRemoveInvestment = (id) => {
    setLocalFormData(prev => ({
      ...prev,
      investments: prev.investments.filter(inv => inv.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        balance: parseFloat(localFormData.balance),
        totalInvested: parseFloat(localFormData.totalInvested),
        totalProfit: parseFloat(localFormData.totalProfit),
        portfolio: {
          stocks: parseFloat(localFormData.portfolio.stocks),
          crypto: parseFloat(localFormData.portfolio.crypto),
          realEstate: parseFloat(localFormData.portfolio.realEstate),
          cash: parseFloat(localFormData.portfolio.cash)
        },
        investments: localFormData.investments
      };

      await updateDoc(doc(db, 'users', user.id), updates);
      onSuccess();
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Edit User: {user?.name}</h2>
          <button onClick={onClose}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Balance</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={localFormData.balance}
                onChange={(e) => setLocalFormData(prev => ({...prev, balance: e.target.value}))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Invested</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={localFormData.totalInvested}
                onChange={(e) => setLocalFormData(prev => ({...prev, totalInvested: e.target.value}))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Profit</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={localFormData.totalProfit}
                onChange={(e) => setLocalFormData(prev => ({...prev, totalProfit: e.target.value}))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio Distribution</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700">Stocks (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.stocks}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, stocks: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Crypto (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.crypto}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, crypto: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Real Estate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.realEstate}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, realEstate: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Cash (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={localFormData.portfolio.cash}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, cash: e.target.value}
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Active Investments Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Active Investments</h3>
            
            {/* Current Investments List */}
            <div className="mb-4 space-y-3">
              {localFormData.investments.map((investment) => (
                <div key={investment.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1 grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{investment.planName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">${parseFloat(investment.amount).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {new Date(investment.startDate).toLocaleDateString()} - {new Date(investment.endDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        investment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {investment.status}
                      </span>
            </div>
          </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveInvestment(investment.id)}
                    className="ml-4 text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add New Investment Form */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Investment</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700">Plan Name</label>
                  <select
                    value={newInvestment.planName}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, planName: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Select Plan</option>
                    <option value="Standard Plan">Standard Plan ($1,000 - $4,000)</option>
                    <option value="Premium Plan">Premium Plan ($5,000 - $9,000)</option>
                    <option value="Advanced Plan">Advanced Plan ($10,000 - $14,000)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700">Amount</label>
                  <input
                    type="number"
                    min="1000"
                    max="14000"
                    value={newInvestment.amount}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, amount: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={newInvestment.startDate}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, startDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddInvestment}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Add Investment
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700"
          >
            Update User
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard; 